import {useDataQuery, useItemQuery} from '@tnc/shared';
import {Fragment, useRef} from 'react';
import {useParams} from 'react-router';

import {usdFormatter} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {Modal, ModalRefT} from '@/components/Modal';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';

function InvoiceDetailsPage() {
  // Hooks
  const {toast} = useNotifications();
  const {
    userAccounts: {admin},
  } = useAuth();
  const {invoiceId: id} = useParams();

  // Data Queries
  const {data: invoice, refetch: refetchInvoice} = useItemQuery({
    table: 'Invoices',
    id: Number(id),
    idColumn: 'invoice_id',
    columns: [
      'invoice_id',
      'invoice_status',
      'invoice_payment_method',
      'invoice_contact_name',
      'invoice_contact_address',
      'invoice_contact_email',
      'invoice_contact_phone',
      'invoice_description',
      'invoice_due_amount',
      'invoice_due_date',
      'invoice_date_issued',
    ],
    enabled: !!id,
  });

  const {data: invoiceItems} = useDataQuery({
    table: 'Invoice_Items',
    columns: [
      'invoice_item_id',
      'invoice_item_quantity',
      'invoice_item_unit_price',
      'invoice_item_total_cost',
      'invoice_item_price_type',
      ['Product', ['product_name', 'product_id']],
      ['Rates', ['factored_price', 'wholesale_price', 'tnc_price']],
    ],
    filters: [
      {
        column: 'fk_invoice_id',
        operator: 'eq',
        value: Number(id),
      },
    ],
    enabled: !!id,
  });

  const print = () => {
    window.print();
  };

  const paymentModalRef = useRef<ModalRefT | null>(null);
  const confirmationModalRef = useRef<ModalRefT | null>(null);

  // Update payment handling
  const handlePayment = async () => {
    try {
      confirmationModalRef.current?.hide();

      const {error} = await SupabaseInstance.supabase
        .from('Invoices')
        .update({invoice_status: 'success', invoice_payment_method: 'cod'})
        .eq('invoice_id', Number(id))
        .select();

      if (error) {
        console.error('Error updating invoice status:', error);
        toast.error({
          title: 'Error Updating Invoice Status',
          message: (error as Error).message,
        });
      }

      // refetch the invoice
      await refetchInvoice();
    } catch (error: unknown) {
      console.error('Error updating invoice status:', error);
      toast.error({
        title: 'Error Updating Invoice Status',
        message: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  if (!invoice || !invoiceItems?.data) {
    return <div>Loading...</div>;
  }

  const invoiceData = invoice;

  return (
    <Fragment>
      <Page currentPage="Invoice Details">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 xl:col-span-12">
            <div className="box">
              <div className="box-header block md:flex">
                <div className="h5 mb-0 block items-center sm:flex">
                  <div className="ms-0 mt-2 flex items-center sm:ms-2 sm:mt-0">
                    <div className="h6 mb-0 font-semibold">
                      INVOICE : <span className="text-primary">#{invoiceData.invoice_id}</span>
                      <span
                        className={`badge !ml-2 inline-flex w-fit !rounded-sm ${
                          invoiceData.invoice_status === 'pending'
                            ? 'bg-warning/10 text-warning'
                            : invoiceData.invoice_status === 'success'
                              ? 'bg-success/10 text-success'
                              : invoiceData.invoice_status === 'failed' || invoiceData.invoice_status === 'canceled'
                                ? 'bg-danger/10 text-danger'
                                : 'bg-info/10 text-info'
                        }`}
                      >
                        {invoiceData.invoice_status}
                      </span>
                      <span className="text-muted !ml-2 text-xs">
                        {(() => {
                          switch (invoiceData.invoice_payment_method) {
                            case 'cod':
                              return 'Cash on Demand';
                            case 'ach':
                              return 'ACH';
                            case 'debit':
                              return 'Debit';
                            case 'credit':
                              return 'Credit';
                            default:
                              return invoiceData.invoice_payment_method;
                          }
                        })()}
                      </span>{' '}
                    </div>
                  </div>
                </div>
                <div className="ms-auto mt-2 md:mt-0">
                  <button
                    type="button"
                    className="ti-btn me-1 bg-secondary !px-2 !py-1 !text-[0.75rem] text-white"
                    onClick={print}
                  >
                    Print<i className="ri-printer-line ms-1 inline-block align-middle"></i>
                  </button>
                  {/* save pdf button */}
                  <button
                    type="button"
                    className="ti-btn me-1 bg-primary !px-2 !py-1 !text-[0.75rem] text-white"
                    onClick={() => null}
                  >
                    Save PDF<i className="ri-file-download-line ms-1 inline-block align-middle"></i>
                  </button>
                  {/* pay button */}
                  {invoiceData.invoice_status === 'pending' && admin && (
                    <button
                      type="button"
                      className="ti-btn me-1 bg-success !px-2 !py-1 !text-[0.75rem] text-white"
                      onClick={() => paymentModalRef.current?.show()}
                    >
                      Pay<i className="ri-money-dollar-circle-line ms-1 inline-block align-middle"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="box-body">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 xl:col-span-12">
                    <div className="grid grid-cols-12 gap-y-6 sm:gap-x-6">
                      <div className="col-span-12 sm:col-span-6 md:col-span-6 lg:col-span-6 xl:col-span-4">
                        <p className="mb-2 text-[#8c9097] dark:text-white/50">Billing Information:</p>
                        <p className="mb-1 font-bold">{invoiceData.invoice_contact_name}</p>
                        <p className="mb-1 text-[#8c9097] dark:text-white/50">{invoiceData.invoice_contact_address}</p>
                        <p className="mb-1 text-[#8c9097] dark:text-white/50">{invoiceData.invoice_contact_email}</p>
                        <p className="mb-1 text-[#8c9097] dark:text-white/50">{invoiceData.invoice_contact_phone}</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-12 xl:col-span-12">
                    <DataTable
                      table="Invoice_Items"
                      columns={[
                        'invoice_item_id',
                        'invoice_item_quantity',
                        'invoice_item_unit_price',
                        'invoice_item_total_cost',
                        'invoice_item_price_type',
                        ['Product', ['product_name', 'product_id']],
                        ['Rates', ['factored_price', 'wholesale_price', 'tnc_price']],
                      ]}
                      columnsDisplay={[
                        {
                          header: 'Product',
                          field: 'Product.product_name',
                          render: item => (
                            <div className="font-semibold">
                              {item.Product.product_name}{' '}
                              <span
                                className={`badge !ml-2 !rounded-sm !text-xs ${
                                  item.invoice_item_price_type === 'wholesale'
                                    ? '!bg-info/10 !text-info'
                                    : '!bg-success/10 !text-success'
                                }`}
                              >
                                {item.invoice_item_price_type} rate
                              </span>
                            </div>
                          ),
                        },
                        {
                          header: 'Quantity',
                          field: 'invoice_item_quantity',
                          render: item => item.invoice_item_quantity,
                        },
                        {
                          header: 'Price/Unit',
                          field: 'invoice_item_unit_price',
                          render: item => usdFormatter.format(item.invoice_item_unit_price),
                        },
                        {
                          header: 'Total',
                          field: 'invoice_item_total_cost',
                          render: item => usdFormatter.format(item.invoice_item_total_cost),
                        },
                      ]}
                      filters={[{column: 'fk_invoice_id', operator: 'eq', value: Number(id)}]}
                      enabled={!!id}
                      hideAdd
                      hideLimit
                    />
                  </div>

                  <div className="col-span-12 xl:col-span-12">
                    <div>
                      <label htmlFor="invoice-note" className="form-label">
                        Description:
                      </label>
                      <p className="form-control w-full !rounded-md !bg-light">{invoiceData.invoice_description}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Page>

      {/* Replace Payment Modal */}
      <Modal ref={paymentModalRef} title="Process Payment" size="sm">
        <div className="p-4">
          <p className="mb-4">Mark this invoice as paid with Cash on Demand?</p>
          <div className="flex justify-end gap-2">
            <button className="ti-btn bg-danger !px-4 !py-2" onClick={() => paymentModalRef.current?.hide()}>
              Cancel
            </button>
            <button
              className="ti-btn bg-success !px-4 !py-2 text-white"
              onClick={() => {
                paymentModalRef.current?.hide();
                confirmationModalRef.current?.show();
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </Modal>

      {/* Replace Confirmation Modal */}
      <Modal ref={confirmationModalRef} title="Confirm Payment" size="sm">
        <div className="p-4">
          <p className="mb-4">Are you sure you want to mark this invoice as paid?</p>
          <div className="flex justify-end gap-2">
            <button className="ti-btn bg-danger !px-4 !py-2" onClick={() => confirmationModalRef.current?.hide()}>
              Cancel
            </button>
            <button className="ti-btn bg-success !px-4 !py-2 text-white" onClick={handlePayment}>
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    </Fragment>
  );
}

export default InvoiceDetailsPage;
